import { useCallback, useEffect, useRef, useState } from "react";
import {
  IDLE_ACTIVITY_EVENTS,
  IDLE_LOGOUT_MS,
  IDLE_WARN_BEFORE_MS,
  idlePhaseAt,
  msUntilIdlePhaseChange,
} from "./idleSession";

type UseIdleSessionLogoutArgs = {
  enabled: boolean;
  onExpire: () => void;
  idleMs?: number;
  warnBeforeMs?: number;
};

/**
 * Tracks user activity; moves to warning then calls onExpire after idleMs.
 * "Stay signed in" / any activity resets the clock.
 */
export function useIdleSessionLogout({
  enabled,
  onExpire,
  idleMs = IDLE_LOGOUT_MS,
  warnBeforeMs = IDLE_WARN_BEFORE_MS,
}: UseIdleSessionLogoutArgs): {
  isWarning: boolean;
  staySignedIn: () => void;
} {
  const [isWarning, setIsWarning] = useState(false);
  const lastActivityRef = useRef(Date.now());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const expiredRef = useRef(false);
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;

  const clearTimer = () => {
    if (timerRef.current != null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const schedule = useCallback(() => {
    clearTimer();
    if (!enabled || expiredRef.current) return;

    const now = Date.now();
    const phase = idlePhaseAt(
      now,
      lastActivityRef.current,
      idleMs,
      warnBeforeMs
    );

    if (phase === "expired") {
      expiredRef.current = true;
      setIsWarning(false);
      onExpireRef.current();
      return;
    }

    setIsWarning(phase === "warning");
    const delay = msUntilIdlePhaseChange(
      now,
      lastActivityRef.current,
      idleMs,
      warnBeforeMs
    );
    timerRef.current = setTimeout(schedule, Math.max(delay, 50));
  }, [enabled, idleMs, warnBeforeMs]);

  const noteActivity = useCallback(() => {
    if (!enabled || expiredRef.current) return;
    lastActivityRef.current = Date.now();
    if (isWarning) setIsWarning(false);
    schedule();
  }, [enabled, isWarning, schedule]);

  const staySignedIn = useCallback(() => {
    expiredRef.current = false;
    lastActivityRef.current = Date.now();
    setIsWarning(false);
    schedule();
  }, [schedule]);

  useEffect(() => {
    if (!enabled) {
      clearTimer();
      setIsWarning(false);
      expiredRef.current = false;
      return;
    }

    expiredRef.current = false;
    lastActivityRef.current = Date.now();
    schedule();

    let throttleUntil = 0;
    const onActivity = () => {
      const now = Date.now();
      if (now < throttleUntil) return;
      throttleUntil = now + 1000;
      noteActivity();
    };

    for (const evt of IDLE_ACTIVITY_EVENTS) {
      window.addEventListener(evt, onActivity, { passive: true });
    }
    return () => {
      clearTimer();
      for (const evt of IDLE_ACTIVITY_EVENTS) {
        window.removeEventListener(evt, onActivity);
      }
    };
  }, [enabled, noteActivity, schedule]);

  return { isWarning, staySignedIn };
}
